import React, { useState, FormEvent, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Send, Sparkles, Database, Globe, Copy, ThumbsUp, ThumbsDown, ChevronDown } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ConversationRail from '../components/ConversationRail';

type Mode = 'internal' | 'search' | 'deep';

type Message = {
  role: 'user' | 'assistant';
  content: string;
  sources?: { rag: Source[]; web: Source[] };
  mode?: Mode;
};

type Source = {
  id: string;          // e.g. "RAG-2" or "WEB-1"
  title: string;
  sourceUrl: string;
  sourceType?: string;
};

// S retained only for fully dynamic values (computed from state at render time)
const S: Record<string, React.CSSProperties> = {
  dot: { width:'7px', height:'7px', borderRadius:'50%', background:'var(--accent)' },
};

const NAMAIChat: React.FC = () => {
  const [messages, setMessages]         = useState<Message[]>([]);
  const [input, setInput]               = useState('');
  const [isLoading, setIsLoading]       = useState(false);
  const [mode, setMode]                 = useState<Mode>('search');
  const [status, setStatus]             = useState<string | null>(null);
  // Server-assigned id for the stored transcript; sent back on each turn so the
  // whole thread is saved as one conversation rather than one per message.
  const [conversationId, setConversationId] = useState<string | null>(null);
  // Bumped after each completed turn so the rail re-fetches (new thread appears,
  // ordering updates).
  const [railRefresh, setRailRefresh] = useState(0);
  const [loadingThread, setLoadingThread] = useState(false);
  const [expanded, setExpanded]         = useState<Record<number,boolean>>({});
  const endRef    = useRef<HTMLDivElement>(null);
  const textaRef  = useRef<HTMLTextAreaElement>(null);
  const seededRef = useRef(false);
  const location  = useLocation();

  // Deep-link from the map: auto-ask the seeded prompt once, then clear the
  // router state so a refresh/back-nav doesn't re-fire it.
  useEffect(() => {
    const seed = (location.state as { seedPrompt?: string } | null)?.seedPrompt;
    if (seed && !seededRef.current) {
      seededRef.current = true;
      window.history.replaceState({}, '');
      send(seed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior:'smooth' }); }, [messages]);
  useEffect(() => {
    if (textaRef.current) {
      textaRef.current.style.height = 'auto';
      textaRef.current.style.height = textaRef.current.scrollHeight + 'px';
    }
  }, [input]);

  const citationMap = (sources?: Message['sources']) => {
    const map: Record<string,string> = {};
    [...(sources?.rag ?? []), ...(sources?.web ?? [])].forEach(s => { map[s.id] = s.sourceUrl; });
    return map;
  };

  const renderWithCitations = (text: string, sources?: Message['sources']): React.ReactNode => {
    const map = citationMap(sources);
    const parts = text.split(/(\[(?:RAG|WEB)-(?:\d+|NAI)\])/);
    return parts.map((p, i) => {
      const m = p.match(/\[(RAG|WEB)-(\d+|NAI)\]/);
      if (m) {
        const key = `${m[1]}-${m[2]}`;
        const url = map[key];
        if (url) return <a key={i} href={url} target="_blank" rel="noopener noreferrer" style={{ color:'var(--accent-strong)', fontSize:'11px', fontFamily:'monospace' }}>{p}</a>;
      }
      return <span key={i}>{p}</span>;
    });
  };

  const send = async (prompt: string) => {
    if (!prompt.trim() || isLoading) return;
    const history = messages.map(m => ({ role: m.role, content: m.content }));
    const assistantIdx = messages.length + 1; // user added at .length, assistant at +1
    setMessages(prev => [...prev,
      { role: 'user', content: prompt },
      { role: 'assistant', content: '', sources: { rag: [], web: [] }, mode },
    ]);
    setIsLoading(true);
    setStatus('retrieving');

    const patch = (fn: (m: Message) => Message) =>
      setMessages(prev => prev.map((m, i) => (i === assistantIdx ? fn(m) : m)));

    try {
      const resp = await fetch('/api/nam-ai/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt, mode, messages: history, conversationId }),
      });
      if (!resp.ok || !resp.body) throw new Error('bad response');
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      // Parse Server-Sent Events (event:/data: pairs separated by a blank line).
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let sep: number;
        while ((sep = buf.indexOf('\n\n')) !== -1) {
          const raw = buf.slice(0, sep); buf = buf.slice(sep + 2);
          const ev = raw.match(/^event: (.*)$/m)?.[1];
          const dataRaw = raw.match(/^data: ([\s\S]*)$/m)?.[1];
          if (!ev || dataRaw == null) continue;
          let data: any; try { data = JSON.parse(dataRaw); } catch { continue; }
          if (ev === 'conversation') setConversationId(data.conversationId);
          else if (ev === 'status') setStatus(data.stage);
          else if (ev === 'token') patch(m => ({ ...m, content: m.content + data.text }));
          else if (ev === 'done') patch(m => ({ ...m, content: data.content ?? m.content, sources: data.sources, mode: data.mode }));
          else if (ev === 'error') patch(m => ({ ...m, content: m.content || data.error }));
        }
      }
    } catch {
      patch(m => ({ ...m, content: m.content || 'Something went wrong. Please try again.' }));
    } finally {
      setIsLoading(false);
      setStatus(null);
      setRailRefresh(n => n + 1);
    }
  };

  // ── Conversation management (rail) ─────────────────────────────────────────
  const startNewChat = () => {
    if (isLoading) return;
    setMessages([]);
    setConversationId(null);
    setExpanded({});
    setInput('');
    textaRef.current?.focus();
  };

  // Reopen a stored thread: its turns become the in-page messages, and the id is
  // sent with the next question so the conversation continues rather than forking.
  const openConversation = async (id: string) => {
    if (isLoading || id === conversationId) return;
    setLoadingThread(true);
    try {
      const r = await fetch(`/api/my/conversations/${id}`, { credentials: 'include' });
      if (!r.ok) throw new Error('not found');
      const data = await r.json();
      setMessages((data.turns as any[]).map(t => ({
        role: t.role,
        content: t.content || (t.error ? 'This response did not complete.' : ''),
        sources: t.sources || { rag: [], web: [] },
        mode: t.mode || undefined,
      })));
      setConversationId(data.id);
      setExpanded({});
    } catch {
      // Thread vanished (deleted elsewhere) — fall back to a clean slate.
      setMessages([]);
      setConversationId(null);
      setRailRefresh(n => n + 1);
    } finally {
      setLoadingThread(false);
    }
  };

  const handleDeleted = (id: string) => {
    if (id === conversationId) { setMessages([]); setConversationId(null); setExpanded({}); }
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const prompt = input;
    setInput('');
    send(prompt);
  };

  const totalSources = (s?: Message['sources']) => (s?.rag?.length ?? 0) + (s?.web?.length ?? 0);

  const SUGGESTIONS = [
    'What are the economic benefits of natural asset management?',
    'Tell me about NAM projects in British Columbia',
    'How do municipalities value natural assets?',
    'What are best practices for wetland restoration?',
  ];

  return (
    <div className="flex h-full bg-transparent overflow-hidden">
      <ConversationRail
        activeId={conversationId}
        onSelect={openConversation}
        onNew={startNewChat}
        refreshKey={railRefresh}
        onDeleted={handleDeleted}
      />

      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
      {/* ── Header ── */}
      <div className="border-b border-[var(--glass-border)] bg-[var(--glass-bg)] backdrop-blur-[16px] shrink-0">
        <div className="max-w-[760px] mx-auto px-6 py-3 flex items-center justify-center">

          <div className="flex bg-[var(--surface-hover)] border border-[var(--glass-border)] rounded-lg p-[2px] gap-[2px]">
            {([
              { id: 'internal', label: 'Internal', Icon: Database, title: 'Curated knowledge base only (Sonnet)' },
              { id: 'search',   label: 'Search',   Icon: Globe,    title: 'Knowledge base + live web search (Sonnet)' },
              { id: 'deep',     label: 'Deep',     Icon: Sparkles, title: 'Deep analysis: knowledge base + web (Opus)' },
            ] as const).map(({ id, label, Icon, title }) => (
              <button
                key={id}
                type="button"
                title={title}
                onClick={() => setMode(id)}
                className="flex items-center gap-[5px] px-[10px] py-1 rounded-md text-[12px] font-medium cursor-pointer border-none transition-all duration-150"
                style={{
                  background: mode===id ? 'var(--surface)' : 'transparent',
                  color: mode===id ? 'var(--text-primary)' : 'var(--text-muted)',
                }}
              >
                <Icon size={12}/> {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Messages ── */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-[760px] mx-auto px-6 pt-10 pb-4 flex flex-col gap-[28px]">
          {loadingThread && (
            <div className="flex flex-col items-center justify-center min-h-[50vh] gap-[10px]">
              <div className="flex items-center gap-[6px]">
                {[0,150,300].map(d => <div key={d} style={{ ...S.dot, animation:`bounce 1s ${d}ms infinite` }} />)}
              </div>
              <p className="text-[13px] text-[color:var(--text-muted)] m-0">Opening conversation…</p>
            </div>
          )}

          {!loadingThread && messages.length === 0 && (
            <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4 text-center">
              <div className="w-[52px] h-[52px] rounded-full bg-[var(--accent-dim)] border border-[var(--accent-border)] flex items-center justify-center">
                <Sparkles size={22} color="var(--accent)" />
              </div>
              <p className="text-[22px] font-semibold text-[color:var(--text-primary)] m-0">How can I help you today?</p>
              <p className="text-[14px] text-[color:var(--text-muted)] m-0">Ask anything about natural asset management</p>
              <div className="grid grid-cols-2 gap-[10px] w-full max-w-[600px]">
                {SUGGESTIONS.map((s,i) => (
                  <button
                    key={i}
                    onClick={() => setInput(s)}
                    className="text-left px-4 py-[14px] bg-[var(--surface)] border border-[var(--border)] hover:border-[var(--border-subtle)] rounded-[12px] text-[13px] text-[color:var(--text-secondary)] hover:text-[color:var(--text-primary)] cursor-pointer leading-[1.5] transition-all duration-150"
                  >{s}</button>
                ))}
              </div>
            </div>
          )}

          {!loadingThread && messages.map((msg, idx) => (msg.role === 'assistant' && !msg.content) ? null : (
            <div key={idx}>
              {msg.role === 'user' ? (
                <div className="flex justify-end">
                  <div className="max-w-[75%] bg-[var(--accent)] backdrop-blur-[8px] rounded-[18px] px-4 py-3 text-[15px] text-white leading-[1.6]">
                    {msg.content}
                  </div>
                </div>
              ) : (
                <div className="flex gap-[14px]">
                  <div className="w-7 h-7 rounded-lg bg-[var(--accent-dim)] border border-[var(--accent-border)] flex items-center justify-center shrink-0 mt-[2px]">
                    <Sparkles size={13} color="var(--accent)" />
                  </div>
                  <div className="flex-1 min-w-0 text-[15px] leading-[1.7] text-[color:var(--text-secondary)]">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        p: ({ children }) => <p className="mb-3 leading-[1.7]">{
                          React.Children.map(children, c => typeof c === 'string' ? renderWithCitations(c, msg.sources) : c)
                        }</p>,
                        li: ({ children }) => <li className="mb-1">{
                          React.Children.map(children, c => typeof c === 'string' ? renderWithCitations(c, msg.sources) : c)
                        }</li>,
                        h3: ({ children }) => <h3 className="text-[15px] font-semibold text-[color:var(--text-primary)] mt-4 mb-2">{children}</h3>,
                        code: ({ children }) => <code className="bg-[var(--surface)] text-[color:var(--accent-strong)] px-[6px] py-[1px] rounded-[4px] text-[13px] font-mono">{children}</code>,
                        strong: ({ children }) => <strong className="text-[color:var(--text-primary)] font-semibold">{children}</strong>,
                        ul: ({ children }) => <ul className="pl-5 mb-3">{children}</ul>,
                        ol: ({ children }) => <ol className="pl-5 mb-3">{children}</ol>,
                      }}
                    >
                      {msg.content}
                    </ReactMarkdown>

                    {totalSources(msg.sources) > 0 && (
                      <div>
                        <button
                          className="flex items-center gap-[6px] text-[12px] text-[color:var(--text-muted)] cursor-pointer bg-transparent border-none pt-2 pb-1 mt-1"
                          onClick={() => setExpanded(p => ({ ...p, [idx]: !p[idx] }))}
                        >
                          <ChevronDown size={13} style={{ transform: expanded[idx] ? 'rotate(180deg)' : 'none', transition:'transform 0.15s' }} />
                          {totalSources(msg.sources)} sources
                        </button>
                        {expanded[idx] && (
                          <div className="mt-2 pl-[14px] border-l-2 border-[var(--border)] flex flex-col gap-1">
                            {msg.sources?.rag?.map(s => (
                              <a
                                key={s.id}
                                href={s.sourceUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[12px] text-[color:var(--text-muted)] hover:text-[color:var(--accent-strong)] no-underline px-2 py-1 rounded-md block transition-colors duration-150"
                              >
                                <span className="text-[color:var(--accent-strong)] font-mono">[{s.id}]</span> {s.title}
                              </a>
                            ))}
                            {msg.sources?.web?.map(s => (
                              <a
                                key={s.id}
                                href={s.sourceUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[12px] text-[color:var(--text-muted)] hover:text-[#93c5fd] no-underline px-2 py-1 rounded-md block transition-colors duration-150"
                              >
                                <span className="text-[#60a5fa] font-mono">[{s.id}]</span> {s.title}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    <div
                      className="flex gap-[2px] mt-2 opacity-40 hover:opacity-100 transition-opacity duration-150"
                    >
                      <button className="p-[6px] rounded-md bg-transparent border-none text-[color:var(--border-subtle)] cursor-pointer flex" title="Copy" onClick={() => navigator.clipboard.writeText(msg.content)}><Copy size={13} /></button>
                      <button className="p-[6px] rounded-md bg-transparent border-none text-[color:var(--border-subtle)] cursor-pointer flex" title="Good"><ThumbsUp size={13} /></button>
                      <button className="p-[6px] rounded-md bg-transparent border-none text-[color:var(--border-subtle)] cursor-pointer flex" title="Bad"><ThumbsDown size={13} /></button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}

          {isLoading && (messages[messages.length-1]?.content ?? '') === '' && (
            <div className="flex gap-[14px]">
              <div className="w-7 h-7 rounded-lg bg-[var(--accent-dim)] border border-[var(--accent-border)] flex items-center justify-center shrink-0 mt-[2px]">
                <Sparkles size={13} color="var(--accent)" />
              </div>
              <div className="flex items-center gap-[8px] pt-[6px]">
                <div className="flex items-center gap-[6px]">
                  {[0,150,300].map(d => (
                    <div key={d} style={{ ...S.dot, animation:`bounce 1s ${d}ms infinite` }} />
                  ))}
                </div>
                <span className="text-[12px] text-[color:var(--text-muted)]">
                  {status === 'searching' ? 'Searching the web…' : status === 'retrieving' ? 'Searching knowledge base…' : 'Thinking…'}
                </span>
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>
      </div>

      {/* ── Input ── */}
      <div className="border-t border-[var(--glass-border)] bg-[var(--glass-bg)] backdrop-blur-[16px] shrink-0">
        <div className="max-w-[760px] mx-auto px-6 pt-[14px] pb-[18px]">
          <form onSubmit={handleSubmit}>
            <div className="bg-[var(--glass-bg)] backdrop-blur-[8px] border border-[var(--glass-border)] rounded-[16px] flex items-end p-1 pl-4 gap-2">
              <textarea
                ref={textaRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key==='Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(e as any); } }}
                placeholder="Ask about natural asset management..."
                rows={1}
                disabled={isLoading}
                className="flex-1 bg-transparent border-none outline-none resize-none text-[15px] text-[color:var(--text-primary)] py-[10px] leading-[1.5] min-h-[44px] max-h-[200px] font-[inherit]"
                style={{ opacity: isLoading ? 0.5 : 1 }}
              />
              <button
                type="submit"
                disabled={isLoading || !input.trim()}
                className="w-9 h-9 rounded-[10px] border-none flex items-center justify-center shrink-0 mb-[2px]"
                style={{
                  background: (!isLoading && input.trim()) ? 'var(--accent)' : 'var(--surface-hover)',
                  color: (!isLoading && input.trim()) ? '#fff' : 'var(--text-muted)',
                  cursor: (!isLoading && input.trim()) ? 'pointer' : 'not-allowed',
                }}
              >
                {isLoading
                  ? <div style={{ width:'14px', height:'14px', border:'2px solid var(--text-muted)', borderTopColor:'transparent', borderRadius:'50%', animation:'spin 0.7s linear infinite' }} />
                  : <Send size={15} />
                }
              </button>
            </div>
            <p className="text-[11px] text-[color:var(--border-subtle)] text-center mt-2">Enter to send · Shift+Enter for new line</p>
          </form>
        </div>
      </div>

      </div>

      <style>{`
        @keyframes bounce { 0%,80%,100%{transform:translateY(0)} 40%{transform:translateY(-6px)} }
        @keyframes spin { to{transform:rotate(360deg)} }
      `}</style>
    </div>
  );
};

export default NAMAIChat;
